import React, {
    useCallback, useMemo,
} from 'react'
import useTranslate from 'helpers/hooks/useTranslate'
import {
    ConstrainMode, DetailsListLayoutMode, IconButton, SelectionMode, ShimmeredDetailsList,
} from '@fluentui/react'
import VehicleEquipment from 'requests/objects/vehicleEquipment'
import styles from 'styles/components/pages/companies/accessibilities/accessibility-list.module.scss'
import flattenObj from 'helpers/methods/flattenObj'
import parseJson from 'helpers/methods/parseJson'

/**
 * @callback EditVehicleEquipmentCallback
 * @param {VehicleEquipment} vehicleEquipment vehicleEquipment
 */

/**
 * @callback DeleteVehicleEquipmentCallback
 * @param {VehicleEquipment} vehicleEquipment vehicleEquipment
 */

/**
 * VehicleEquipmentList
 * @param {object} props Props
 * @param {object} props.lang Lang
 * @param {VehicleEquipment[]} props.items items
 * @param {EditVehicleEquipmentCallback} props.editVehicleEquipment editVehicleEquipment
 * @param {DeleteVehicleEquipmentCallback} props.deleteVehicleEquipment deleteVehicleEquipment
 * @returns {JSX.Element} Element
 */
export default function VehicleEquipmentList({
    lang,
    items,
    editVehicleEquipment,
    deleteVehicleEquipment,
}) {
    const { tKey, tObj } = useTranslate({ lang })

    const vehicleEquipments = useMemo(() => items.map(x => flattenObj(x)), [items])

    const ActionButtons = useCallback(
        vehicleEquipment => (
            <div className={styles['action-buttons']}>
                <IconButton
                    iconProps={{ iconName: 'Edit' }}
                    title={tKey('edit')}
                    onClick={() => editVehicleEquipment(vehicleEquipment)}
                />
                <IconButton
                    iconProps={{ iconName: 'Delete' }}
                    title={tKey('delete')}
                    onClick={() => deleteVehicleEquipment(vehicleEquipment)}
                />
            </div>
        ), [deleteVehicleEquipment, editVehicleEquipment, tKey],
    )

    const columns = useMemo(() => /** @type {import('@fluentui/react').IColumn[]} */([
        {
            key: 'name',
            name: tKey('name'),
            fieldName: 'name',
            minWidth: 50,
            maxWidth: 200,
            isResizable: true,
            onRender: item => tObj(parseJson(item.name)),
        },
        {
            key: 'code',
            name: tKey('code'),
            fieldName: 'vehicleEquipmentTmsId',
            minWidth: 50,
            maxWidth: 200,
            isResizable: true,
        },
        {
            key: 'actions',
            name: '',
            minWidth: 96,
            maxWidth: 96,
            isResizable: false,
            onRender: ActionButtons,
        },
    ]), [tKey, ActionButtons, tObj])

    return (
        <div className={styles['accessibility-list']}>
            <ShimmeredDetailsList
                items={vehicleEquipments}
                columns={columns}
                selectionMode={SelectionMode.none}
                onShouldVirtualize={() => true}
                layoutMode={DetailsListLayoutMode.justified}
                constrainMode={ConstrainMode.unconstrained}
                onRenderDetailsHeader={(props, defaultRender) => defaultRender({ ...props })}
            />
        </div>
    )
}
